import React, { useState, useRef } from "react";
import "./Quiz.css"; // keep your existing CSS
import parse from 'html-react-parser';
import DOMPurify from 'dompurify';

export default function Quiz({ quizData, title }) {
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [selectedOption, setSelectedOption] = useState(null);
  const [answers, setAnswers] = useState([]);
  const [showFeedback, setShowFeedback] = useState(false);
  const [quizComplete, setQuizComplete] = useState(false);

  const question = quizData[currentQuestion];

  const handleOptionSelect = (index) => {
    if (showFeedback) return;
    setSelectedOption(index);
  };

  const handleSubmit = () => {
    if (selectedOption === null) return;
    const isCorrect = selectedOption === question.correctAnswer;

    setAnswers((prev) => [
      ...prev,
      {
        questionId: question.id,
        selectedOption,
        correctAnswer: question.correctAnswer,
        isCorrect
      }
    ]);

    setShowFeedback(true);
  };

  const handleNext = () => {
    if (currentQuestion < quizData.length - 1) {
      setCurrentQuestion((prev) => prev + 1);
      setSelectedOption(null);
      setShowFeedback(false);
      setTimeout(() => {
        if (questionRef.current) {
          const elementTop = questionRef.current.getBoundingClientRect().top + window.pageYOffset;
          const offset = 120; // 👈 adjust this value to taste
          window.scrollTo({
            top: elementTop - offset,
            behavior: "smooth",
          });
        }
      }, 100);
    } else {
      setQuizComplete(true);
      // send event to GA, see reports where event name = quiz_complete
      if (typeof window.gtag === 'function') {
        const correctCount = answers.filter((a) => a.isCorrect).length + 
                            (selectedOption === question.correctAnswer ? 1 : 0); 
        const score = Math.round((correctCount / quizData.length) * 100);

        window.gtag('event', 'quiz_complete', {
          quiz_name: title,
          score: score,
          correct_answers: correctCount,
          total_questions: quizData.length
        });
      }
    }
  };

  const handleRestart = () => {
    setCurrentQuestion(0);
    setSelectedOption(null);
    setAnswers([]);
    setShowFeedback(false);
    setQuizComplete(false);
  };

  const correctCount = answers.filter((a) => a.isCorrect).length;
  const score = Math.round((correctCount / quizData.length) * 100);
  const questionRef = useRef(null);

  return (
    <div className="quiz-container">
      {!quizComplete ? (
        <div className="question-card" ref={questionRef}>
          <p className="quiz-progress-text">
            {currentQuestion + 1} of {quizData.length}
          </p>
          <div className="progress-bar">
            <div
              className="progress-fill"
              style={{ width: `${((currentQuestion + 1) / quizData.length) * 100}%` }}
            ></div>
          </div>

          <h4 className="question-title">{parse(DOMPurify.sanitize(question.question))}</h4>

          <div className="options-container">
            {question.options.map((option, index) => (
              <button
                key={index}
                className={`option-button ${
                  selectedOption === index ? "selected" : ""
                } ${
                  showFeedback && index === question.correctAnswer ? "correct" : ""
                } ${
                  showFeedback &&
                  index === selectedOption &&
                  index !== question.correctAnswer
                    ? "incorrect"
                    : ""
                }`}
                onClick={() => handleOptionSelect(index)}
                disabled={showFeedback}
              >
                <div className="option-content">
                  <p className="option-letter">{String.fromCharCode(65 + index)}</p>
                  <p className="option-text">{parse(DOMPurify.sanitize(option))}</p>
                </div>
                <div className="option-icons">
                  <svg
                    className="option-icon tick"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="3"
                  >
                    <path d="M20 6L9 17l-5-5" />
                  </svg>
                  <svg
                    className="option-icon cross"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="3"
                  >
                    <path d="M18 6L6 18" />
                    <path d="M6 6l12 12" />
                  </svg>
                </div>
              </button>
            ))}
          </div>

          {showFeedback && (
            <div className="explanation-box show">
              <p className="explanation-title">Explanation:</p>
              <p>{parse(DOMPurify.sanitize(question.explanation))}</p>
            </div>
          )}

          <div className="button-container">
            {!showFeedback ? (
              <button
                className="btn_green_fill"
                onClick={handleSubmit}
                disabled={selectedOption === null}
              >
                Submit answer
              </button>
            ) : (
              <button className="btn_green_fill" onClick={handleNext}>
                {currentQuestion < quizData.length - 1
                  ? "Next question"
                  : "View results"}
              </button>
            )}
          </div>
        </div>
      ) : (
        <div className="question-card results-screen show">
          <h2 className={`score-circle ${score >= 60 ? "score-excellent" : "score-good"}`}>
            {score}%
          </h2>
          <h2 className="results-title">Quiz Complete!</h2>
          <p className="results-message">
            {score >= 80
              ? "Excellent work! You've mastered this topic."
              : score >= 60
              ? "Good job! You're on the right track."
              : score >= 40
              ? "Not bad, but there's room for improvement."
              : "Keep practising - you'll get there!"}
          </p>

          <div className="stats-grid">
            <div className="stat-item">
              <h3 className="stat-number">{quizData.length}</h3>
              <p className="stat-label">Questions</p>
            </div>
            <div className="stat-item">
              <h3 className="stat-number correct">{correctCount}</h3>
              <p className="stat-label">Correct</p>
            </div>
            <div className="stat-item">
              <h3 className="stat-number incorrect">
                {quizData.length - correctCount}
              </h3>
              <p className="stat-label">Incorrect</p>
            </div>
          </div>

          <div className="review-section">
            <h3 className="review-title">Review:</h3>
            <div className="review-container">
              {quizData.map((q, index) => {
                const userAnswer = answers[index];
                if (!userAnswer) return null;
                const isCorrect = userAnswer.isCorrect;
                return (
                  <div key={q.id} className="review-item">
                    <div className={`review-icon ${isCorrect ? "correct" : "incorrect"}`}>
                      <svg
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke={isCorrect ? "#14EF7C" : "#FF4638"}
                        strokeWidth="3"
                      >
                        {isCorrect ? (
                          <path d="M20 6L9 17l-5-5" />
                        ) : (
                          <>
                            <path d="M18 6L6 18" />
                            <path d="M6 6l12 12" />
                          </>
                        )}
                      </svg>
                    </div>
                    <div className="review-content">
                      <p className="review-question">{parse(DOMPurify.sanitize(q.question))}</p>
                      <p
                        className={`review-answer review-user-answer ${
                          isCorrect ? "correct" : "incorrect"
                        }`}
                      >
                        Your answer: {parse(DOMPurify.sanitize(q.options[userAnswer.selectedOption]))}
                      </p>
                      {!isCorrect && (
                        <p className="review-answer review-correct-answer">
                          Correct answer: {parse(DOMPurify.sanitize(q.options[q.correctAnswer]))}
                        </p>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          <button className="btn_green_fill" onClick={handleRestart}>
            Retake quiz
          </button>
        </div>
      )}
    </div>
  );
}
